import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = React.useState({
    nombre: '',
    email: '',
    mensaje: ''
  });
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ nombre: '', email: '', mensaje: '' });
        // Importante: Resetear a 'idle' para que el botón vuelva a la normalidad
        setTimeout(() => {
          setStatus('idle');
        }, 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <div id="contacto" className="relative bg-primary py-24 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-blue-800 opacity-50 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-blue-600 opacity-20 blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Column: Info & Text */}
          <div className="text-white space-y-8">
            <div>
              <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
                Hablemos de su proyecto
              </h2>
              <p className="mt-4 text-xl text-blue-100 max-w-lg">
                Estamos listos para diseñar la solución perfecta para su evento o instalación. Contáctenos hoy mismo y reciba una propuesta a medida.
              </p>
            </div>

            <div className="space-y-6">
              {/* Phones Block */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-blue-800 p-3 rounded-lg">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-white">Llámenos</h3>
                  <div className="mt-2 space-y-1 text-blue-100">
                    <p className="hover:text-white transition-colors cursor-pointer">+34 636 39 95 50</p>
                    <p className="hover:text-white transition-colors cursor-pointer">+34 625 88 34 12</p>
                    <p className="hover:text-white transition-colors cursor-pointer">+34 698 77 90 51</p>
                    <p className="text-sm text-yellow-400 font-semibold mt-1">Disponibles 24/7</p>
                  </div>
                </div>
              </div>

              {/* Email Block */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-blue-800 p-3 rounded-lg">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-white">Escríbanos</h3>
                  <p className="mt-2 text-blue-100">administracion@aresglobalcontrol.com</p>
                </div>
              </div>

              {/* Address Block */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-blue-800 p-3 rounded-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-white">Visítenos</h3>
                  <p className="mt-2 text-blue-100">
                    Av. de la Constitución, 119<br />
                    La Zaidía, 46009 Valencia
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Floating Form Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10 lg:p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Envíenos un mensaje</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nombre completo</label>
                <input 
                  type="text" 
                  name="nombre" 
                  id="name" 
                  required
                  value={formData.nombre}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-3 px-4 focus:ring-primary focus:border-primary bg-gray-50" 
                  placeholder="Su nombre"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Correo electrónico</label>
                <input 
                  type="email" 
                  name="email" 
                  id="email" 
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-3 px-4 focus:ring-primary focus:border-primary bg-gray-50" 
                  placeholder="ejemplo@empresa.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">¿En qué podemos ayudarle?</label>
                <textarea 
                  id="message" 
                  name="mensaje" 
                  rows={4} 
                  required
                  value={formData.mensaje}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-3 px-4 focus:ring-primary focus:border-primary bg-gray-50"
                  placeholder="Detalles de su consulta..."
                ></textarea>
              </div>

              <button 
                type="submit" 
                disabled={status === 'sending' || status === 'success'}
                className={`w-full flex justify-center py-4 px-4 border border-transparent rounded-md shadow-lg text-sm font-medium text-white transition-all transform hover:-translate-y-1 ${
                  status === 'sending' 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : status === 'success'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-primary hover:bg-blue-900'
                }`}
              >
                {status === 'sending' ? 'Enviando...' : status === 'success' ? '¡Mensaje Enviado!' : 'Solicitar Información'}
              </button>
              
              {status === 'success' && (
                <p className="text-green-600 text-center text-sm font-medium bg-green-50 p-3 rounded">
                  Gracias. Nos pondremos en contacto muy pronto.
                </p>
              )}
              {status === 'error' && (
                <p className="text-red-600 text-center text-sm font-medium bg-red-50 p-3 rounded">
                  Error al enviar. Por favor llámenos directamente.
                </p>
              )}
            </form>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Contact;
